import type { ReactNode, SyntheticEvent } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import type { DialogProps } from '@mui/material';
import {
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

import { useTestId } from '@hooks/useTestId';

import { CloseIconButton, StyledDialog } from './controls';

type ModalProps = {
  readonly open: boolean;
  readonly title?: React.ReactNode;
  readonly text?: React.ReactNode | string;
  readonly className?: string;
  readonly dialogButtons?: ReactNode;
  readonly dialogProps?: Partial<DialogProps>;
  readonly children?: React.ReactNode;
  readonly onClose?: () => void;
  readonly backdropClick?: boolean;
  readonly testID?: string;
};

export const Modal = (props: ModalProps) => {
  const {
    title,
    text,
    open,
    dialogProps,
    dialogButtons,
    children,
    onClose,
    backdropClick,
    testID = 'modal',
  } = props;

  const handleClose = (event: SyntheticEvent, reason?: string) => {
    if (reason !== 'backdropClick' || backdropClick) {
      onClose?.();
    }
  };

  const getTestId = useTestId(testID);

  return (
    <StyledDialog
      fullWidth
      keepMounted
      onClose={handleClose}
      {...dialogProps}
      open={open}
    >
      {title && (
        <DialogTitle data-testid={getTestId('title')}>{title}</DialogTitle>
      )}

      <DialogContent>
        {text && (
          <DialogContentText
            data-testid={getTestId('content')}
            color="textPrimary"
          >
            {text}
          </DialogContentText>
        )}
        {onClose && (
          <CloseIconButton
            data-testid={getTestId('close')}
            onClick={onClose}
          >
            <CloseIcon />
          </CloseIconButton>
        )}
        {children}
      </DialogContent>

      {dialogButtons && <DialogActions>{dialogButtons}</DialogActions>}
    </StyledDialog>
  );
};
